import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { Heading } from "../heading";
import { Button } from "../button";
import { Input } from "../input";
import { Textarea } from "../textarea";
import { Switch } from "../switch";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import TagInput from "./TagInput";
import MarkdownEditor from "./MarkdownEditor";
import BlogPreviewModal from "./BlogPreviewModal";
import { isSlugTaken, slugify } from "./slugUtils";
import { EyeIcon } from "@heroicons/react/20/solid";

// Timestamp/Date → valor de <input type="datetime-local"> en hora local.
function toDatetimeLocal(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const BRAND_CTA =
  "inline-flex items-center gap-2 rounded-full bg-[#1b4d2e] px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#153d24] disabled:opacity-60";
const BRAND_GHOST =
  "inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition hover:border-[#1b4d2e] hover:text-[#1b4d2e] disabled:opacity-60";
const PILL_GROUP = "inline-flex items-center gap-1 rounded-full bg-gray-100 p-1";
const PILL_IDLE =
  "rounded-full px-4 py-1.5 text-sm text-gray-600 transition hover:text-[#1b4d2e]";
const PILL_ACTIVE =
  "rounded-full bg-[#1b4d2e] px-4 py-1.5 text-sm font-semibold text-white shadow-sm";

const EMPTY_TRANSLATION = {
  title: "",
  excerpt: "",
  body: "",
  seoTitle: "",
  seoDescription: "",
  ogImage: "",
};

function makePostId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `post-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function BlogForm() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { user, userInfo } = useAuth();

  // Stable post id — used for image storage paths even before first save
  const [postId] = useState(() => routeId || makePostId());
  const isEditing = Boolean(routeId);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("draft");
  const [coverImage, setCoverImage] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [tags, setTags] = useState([]);
  const [translations, setTranslations] = useState({ es: { ...EMPTY_TRANSLATION }, en: { ...EMPTY_TRANSLATION } });
  const [slugEs, setSlugEs] = useState("");
  const [slugEn, setSlugEn] = useState("");
  const [hasEs, setHasEs] = useState(true);
  const [hasEn, setHasEn] = useState(false);
  const [activeLang, setActiveLang] = useState("es");
  const [slugError, setSlugError] = useState({ es: "", en: "" });
  const slugDirtyRef = useRef({ es: false, en: false });
  const [originalPublishedAt, setOriginalPublishedAt] = useState(null);
  // Fecha de publicación editable (input datetime-local, en la hora del
  // navegador). Vacía = "ahora" al guardar como publicado. Antes la fecha era
  // invisible y un post sembrado con fecha futura se publicaba "sin verse".
  const [publishedAtInput, setPublishedAtInput] = useState("");
  const coverInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load existing post for editing
  useEffect(() => {
    if (!isEditing) {
      setLoading(false);
      // Default author on a new post = current user's display name / email
      setAuthorName(userInfo?.name || user?.email || "");
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "blogPosts", routeId));
        if (!snap.exists()) {
          showAlert("Post no encontrado", "error");
          navigate("/dashboard/blog");
          return;
        }
        const data = snap.data();
        setStatus(data.status || "draft");
        setCoverImage(data.coverImage || "");
        setAuthorName(data.authorName || userInfo?.name || user?.email || "");
        setTags(data.tags || []);
        setSlugEs(data.slugEs || "");
        setSlugEn(data.slugEn || "");
        setHasEs(Boolean(data.translations?.es));
        setHasEn(Boolean(data.translations?.en));
        setTranslations({
          es: { ...EMPTY_TRANSLATION, ...(data.translations?.es || {}) },
          en: { ...EMPTY_TRANSLATION, ...(data.translations?.en || {}) },
        });
        setOriginalPublishedAt(data.publishedAt || null);
        setPublishedAtInput(toDatetimeLocal(data.publishedAt));
        // If slugs are set, user clearly customized them at some point
        slugDirtyRef.current = {
          es: Boolean(data.slugEs),
          en: Boolean(data.slugEn),
        };
      } catch (e) {
        console.error(e);
        showAlert("Error cargando el post", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEditing, routeId, navigate, user, userInfo]);

  function updateTranslation(lang, field, val) {
    setTranslations((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: val },
    }));
  }

  function handleTitleBlur(lang) {
    const title = translations[lang].title;
    if (!slugDirtyRef.current[lang] && title) {
      const newSlug = slugify(title);
      if (lang === "es") setSlugEs(newSlug);
      else setSlugEn(newSlug);
    }
  }

  async function validateSlug(lang, slug) {
    if (!slug) {
      setSlugError((p) => ({ ...p, [lang]: "El slug es requerido." }));
      return false;
    }
    const taken = await isSlugTaken(db, lang, slug, postId);
    if (taken) {
      setSlugError((p) => ({ ...p, [lang]: "Este slug ya está en uso." }));
      return false;
    }
    setSlugError((p) => ({ ...p, [lang]: "" }));
    return true;
  }

  async function handleCoverFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `blog/${postId}/cover-${Date.now()}-${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setCoverImage(url);
    } catch (err) {
      console.error(err);
      showAlert("Error subiendo la portada", "error");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  }

  const langsEnabled = useMemo(() => {
    const list = [];
    if (hasEs) list.push("es");
    if (hasEn) list.push("en");
    return list;
  }, [hasEs, hasEn]);

  async function handleSubmit() {
    if (langsEnabled.length === 0) {
      showAlert("Agrega al menos un idioma (Español o Inglés)", "error");
      return;
    }

    // Validate each enabled language
    for (const lang of langsEnabled) {
      if (!translations[lang].title?.trim()) {
        showAlert(`Falta el título en ${lang === "es" ? "Español" : "Inglés"}`, "error");
        setActiveLang(lang);
        return;
      }
      if (!translations[lang].body?.trim()) {
        showAlert(`Falta el contenido en ${lang === "es" ? "Español" : "Inglés"}`, "error");
        setActiveLang(lang);
        return;
      }
      const s = lang === "es" ? slugEs : slugEn;
      const ok = await validateSlug(lang, s);
      if (!ok) {
        setActiveLang(lang);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        status,
        authorUid: user?.uid || null,
        authorName: authorName?.trim() || userInfo?.name || user?.email || null,
        coverImage: coverImage || null,
        tags,
        slugEs: hasEs ? slugEs : null,
        slugEn: hasEn ? slugEn : null,
        translations: {
          es: hasEs ? translations.es : null,
          en: hasEn ? translations.en : null,
        },
        updatedAt: serverTimestamp(),
      };
      if (!isEditing) {
        payload.createdAt = serverTimestamp();
      }
      // Fecha de publicación: la del campo si el usuario la fijó; si está
      // vacío y el post queda publicado, ahora. Un borrador conserva la fecha
      // que tenga (programación) para que volver a publicarlo no la pierda.
      const fechaElegida = publishedAtInput ? new Date(publishedAtInput) : null;
      if (fechaElegida && !isNaN(fechaElegida.getTime())) {
        payload.publishedAt = Timestamp.fromDate(fechaElegida);
      } else if (status === "published") {
        payload.publishedAt = originalPublishedAt || serverTimestamp();
      } else {
        payload.publishedAt = originalPublishedAt || null;
      }

      await setDoc(doc(db, "blogPosts", postId), payload, { merge: false });
      showAlert("Post guardado", "success");
      navigate("/dashboard/blog");
    } catch (e) {
      console.error(e);
      showAlert("Error guardando el post", "error");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-6">Cargando...</div>;

  const t = translations[activeLang];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <Button plain onClick={() => navigate("/dashboard/blog")}>
          <ArrowLeftIcon className="h-4 w-4" />
          Volver
        </Button>
        <Heading level={1}>{isEditing ? "Editar post" : "Nuevo post"}</Heading>
      </div>

      {/* Top-level fields */}
      <div className="mb-6 space-y-4 rounded-lg border border-gray-200 bg-white p-4">
        {/* Cover image */}
        <div>
          <label className="block mb-1 font-medium">Portada</label>
          <p className="mb-2 text-xs text-gray-500">
            Tamaño recomendado: <strong>1200 × 630 px</strong>. Se usa como
            miniatura en el grid del blog y como imagen al compartir en redes
            sociales (Facebook, LinkedIn, Twitter, WhatsApp).
          </p>
          <div className="flex items-center gap-4">
            {coverImage && (
              <img
                src={coverImage}
                alt="cover"
                className="h-24 w-40 rounded object-cover"
              />
            )}
            <div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverFile}
              />
              <Button
                type="button"
                outline
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
              >
                {uploadingCover ? "Subiendo..." : coverImage ? "Cambiar portada" : "Subir portada"}
              </Button>
              {coverImage && (
                <Button
                  type="button"
                  plain
                  onClick={() => setCoverImage("")}
                  className="ml-2"
                >
                  Quitar
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Author */}
        <div>
          <label className="block mb-1 font-medium">Autor</label>
          <Input
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Nombre del autor"
          />
          <p className="mt-1 text-xs text-gray-500">
            Nombre que se muestra públicamente en el post. Puedes cambiarlo sin
            afectar quién hizo la edición.
          </p>
        </div>

        {/* Tags */}
        <TagInput value={tags} onChange={setTags} activeLang={activeLang} />

        {/* Status + fecha de publicación */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={status === "published"}
              onChange={(v) => setStatus(v ? "published" : "draft")}
            />
            <span className="text-sm">
              Estado: <strong>{status === "published" ? "Publicado" : "Borrador"}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600" htmlFor="blog-published-at">
              Fecha de publicación
            </label>
            <input
              id="blog-published-at"
              type="datetime-local"
              value={publishedAtInput}
              onChange={(e) => setPublishedAtInput(e.target.value)}
              className="rounded-lg border border-zinc-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
            {publishedAtInput && (
              <button
                type="button"
                onClick={() => setPublishedAtInput("")}
                className="text-xs text-gray-500 hover:underline"
                title="Vaciar: al guardar como publicado toma la fecha y hora de ahora"
              >
                Ahora
              </button>
            )}
          </div>
          {status === "published" && publishedAtInput && new Date(publishedAtInput) > new Date() && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
              Programado: se verá en el sitio a partir del{" "}
              {new Date(publishedAtInput).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
            </span>
          )}
          {status === "published" && !publishedAtInput && (
            <span className="text-xs text-gray-500">Se publica en cuanto guardes.</span>
          )}
        </div>
      </div>

      {/* Language tabs */}
      <div className="mb-4 flex items-center justify-between">
        <div className={PILL_GROUP}>
          {[
            { key: "es", label: "Español" },
            { key: "en", label: "English" },
          ].map((l) => {
            const enabled = l.key === "es" ? hasEs : hasEn;
            const isActive = activeLang === l.key;
            return (
              <button
                key={l.key}
                type="button"
                onClick={() => setActiveLang(l.key)}
                className={`${isActive ? PILL_ACTIVE : PILL_IDLE} ${
                  !enabled ? "opacity-60" : ""
                }`}
              >
                {l.label} {!enabled && "(desactivado)"}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 text-sm">
          {activeLang === "es" && (
            <Button
              plain
              type="button"
              onClick={() => {
                if (hasEs && langsEnabled.length > 1) {
                  setHasEs(false);
                  setTranslations((p) => ({ ...p, es: { ...EMPTY_TRANSLATION } }));
                  setSlugEs("");
                } else {
                  setHasEs(true);
                }
              }}
            >
              {hasEs ? "Quitar Español" : "Añadir Español"}
            </Button>
          )}
          {activeLang === "en" && (
            <Button
              plain
              type="button"
              onClick={() => {
                if (hasEn && langsEnabled.length > 1) {
                  setHasEn(false);
                  setTranslations((p) => ({ ...p, en: { ...EMPTY_TRANSLATION } }));
                  setSlugEn("");
                } else {
                  setHasEn(true);
                }
              }}
            >
              {hasEn ? "Quitar English" : "Añadir English"}
            </Button>
          )}
        </div>
      </div>

      {/* Active translation fields */}
      {((activeLang === "es" && hasEs) || (activeLang === "en" && hasEn)) && (
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <label className="block mb-1 font-medium">Título</label>
            <Input
              value={t.title}
              onChange={(e) => updateTranslation(activeLang, "title", e.target.value)}
              onBlur={() => handleTitleBlur(activeLang)}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Slug</label>
            <Input
              value={activeLang === "es" ? slugEs : slugEn}
              onChange={(e) => {
                slugDirtyRef.current[activeLang] = true;
                const v = slugify(e.target.value);
                if (activeLang === "es") setSlugEs(v);
                else setSlugEn(v);
                setSlugError((p) => ({ ...p, [activeLang]: "" }));
              }}
              onBlur={() =>
                validateSlug(activeLang, activeLang === "es" ? slugEs : slugEn)
              }
            />
            <p className="mt-1 text-xs text-gray-500">
              URL: /{activeLang === "en" ? "en/" : ""}blog/
              {(activeLang === "es" ? slugEs : slugEn) || "..."}
            </p>
            {slugError[activeLang] && (
              <p className="mt-1 text-xs text-red-600">{slugError[activeLang]}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 font-medium">
              Extracto ({t.excerpt.length}/160)
            </label>
            <Textarea
              rows={2}
              value={t.excerpt}
              onChange={(e) =>
                updateTranslation(activeLang, "excerpt", e.target.value.slice(0, 200))
              }
            />
          </div>

          <MarkdownEditor
            label="Contenido"
            postId={postId}
            value={t.body}
            onChange={(val) => updateTranslation(activeLang, "body", val)}
          />

          <details className="mt-2">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              SEO avanzado
            </summary>
            <div className="mt-3 space-y-3 rounded-md bg-gray-50 p-3">
              <div>
                <label className="block mb-1 text-sm font-medium">
                  SEO Title (opcional)
                </label>
                <Input
                  value={t.seoTitle}
                  onChange={(e) =>
                    updateTranslation(activeLang, "seoTitle", e.target.value)
                  }
                  placeholder="Por defecto usa el título del post"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">
                  SEO Description (opcional)
                </label>
                <Textarea
                  rows={2}
                  value={t.seoDescription}
                  onChange={(e) =>
                    updateTranslation(activeLang, "seoDescription", e.target.value)
                  }
                  placeholder="Por defecto usa el extracto"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">
                  OG Image URL (opcional)
                </label>
                <Input
                  value={t.ogImage}
                  onChange={(e) =>
                    updateTranslation(activeLang, "ogImage", e.target.value)
                  }
                  placeholder="Por defecto usa la portada"
                />
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Footer actions */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <button
          type="button"
          className={BRAND_GHOST}
          onClick={() => setPreviewOpen(true)}
          disabled={saving}
        >
          <EyeIcon className="h-4 w-4" />
          Vista previa
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={BRAND_GHOST}
            onClick={() => navigate("/dashboard/blog")}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={BRAND_CTA}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar post"}
          </button>
        </div>
      </div>

      <BlogPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        post={{
          status,
          coverImage,
          tags,
          slugEs: hasEs ? slugEs : null,
          slugEn: hasEn ? slugEn : null,
          translations: {
            es: hasEs ? translations.es : null,
            en: hasEn ? translations.en : null,
          },
        }}
        authorName={authorName?.trim() || userInfo?.name || user?.email || ""}
      />
    </div>
  );
}
